import { createAsyncThunk } from '@reduxjs/toolkit'
import { Comment } from '../models/Comment'
import {
  addComment,
  addCommentReply,
  deleteComment,
  setCommentReplies,
  setComments,
  updateComment,
} from '../actions/comments'
import { ConstructionTab } from '../reducers/construction'
import { BASE_URL } from '../config'
import { getIDToken } from '../utils/AuthUtil'

export const fetchCommentsAsync = createAsyncThunk(
  'comments/fetchComments',
  async (payload: { tab: ConstructionTab; file_id?: number }, { dispatch }) => {
    const url = new URL(`${BASE_URL}/api/comments`)
    if (payload.file_id !== undefined) {
      const params = [['file_id', payload.file_id?.toString()]]
      url.search = new URLSearchParams(params).toString()
    }

    fetch(url, {
      method: 'GET',
      headers: {
        'Access-Control-Allow-Origin': '*',
        Authorization: `Bearer ${await getIDToken()}`,
      },
    })
      .then((response) => {
        if (response.ok) {
          return response.json()
        } else {
          throw new Error(response.statusText)
        }
      })
      .then((data) => {
        dispatch(setComments({ tab: payload.tab, comments: data }))
      })
      .catch((error) => {
        console.error('Error during fetching comments: ', error)
      })
  },
)

export const fetchCommentAsync = createAsyncThunk(
  'comments/fetchComment',
  async (
    payload: { tab: ConstructionTab; commentId: number },
    { dispatch },
  ) => {
    const url = `${BASE_URL}/api/comments/${payload.commentId}`
    fetch(url, {
      method: 'GET',
      headers: {
        'Access-Control-Allow-Origin': '*',
        Authorization: `Bearer ${await getIDToken()}`,
      },
    })
      .then((response) => {
        if (response.ok) {
          return response.json()
        } else {
          throw new Error(response.statusText)
        }
      })
      .catch((error) => {
        console.error(
          `Error during fetching a comment ${payload.commentId}: `,
          error,
        )
      })
  },
)

export const fetchCommentRepliesAsync = createAsyncThunk(
  'comments/fetchCommentReplies',
  async (
    payload: { tab: ConstructionTab; commentId: number },
    { dispatch },
  ) => {
    const url = `${BASE_URL}/api/comments/${payload.commentId}/replies`
    fetch(url, {
      method: 'POST',
      headers: {
        'Access-Control-Allow-Origin': '*',
        Authorization: `Bearer ${await getIDToken()}`,
      },
    })
      .then((response) => {
        if (response.ok) {
          return response.json()
        } else {
          throw new Error(response.statusText)
        }
      })
      .then((data) => {
        dispatch(
          setCommentReplies({
            tab: payload.tab,
            commentId: payload.commentId,
            replies: data,
          }),
        )
      })
      .catch((error) => {
        console.error(`Error during fetching comment replies: `, error)
      })
  },
)

export const addCommentAsync = createAsyncThunk(
  'comments/addComment',
  async (payload: { tab: ConstructionTab; comment: Comment }, { dispatch }) => {
    const url = `${BASE_URL}/api/comments`
    fetch(url, {
      method: 'POST',
      headers: {
        'Access-Control-Allow-Origin': '*',
        Authorization: `Bearer ${await getIDToken()}`,
      },
      body: JSON.stringify(payload.comment),
    })
      .then((response) => {
        if (response.ok) {
          return response.json()
        } else {
          throw new Error(response.statusText)
        }
      })
      .then((data) => {
        dispatch(addComment({ tab: payload.tab, comment: data }))
      })
      .catch((error) => {
        console.error('Error during adding a comment: ', error)
      })
  },
)

export const addCommentReplyAsync = createAsyncThunk(
  'comments/addCommentReply',
  async (
    payload: { tab: ConstructionTab; commentId: number; reply: Comment },
    { dispatch },
  ) => {
    const url = `${BASE_URL}/api/comments/${payload.commentId}/replies`
    fetch(url, {
      method: 'POST',
      headers: {
        'Access-Control-Allow-Origin': '*',
        Authorization: `Bearer ${await getIDToken()}`,
      },
      body: JSON.stringify(payload.reply),
    })
      .then((response) => {
        if (response.ok) {
          return response.json()
        } else {
          throw new Error(response.statusText)
        }
      })
      .then((data) => {
        dispatch(
          addCommentReply({
            tab: payload.tab,
            commentId: payload.commentId,
            reply: data,
          }),
        )
      })
      .catch((error) => {
        console.error(`Error during adding comment reply: `, error)
      })
  },
)

export const updateCommentAsync = createAsyncThunk(
  'comments/updateComment',
  async (
    payload: {
      tab: ConstructionTab
      commentId: number
      newComment: Comment
    },
    { dispatch },
  ) => {
    const url = `${BASE_URL}/api/comments/${payload.commentId}`
    fetch(url, {
      method: 'PUT',
      headers: {
        'Access-Control-Allow-Origin': '*',
        Authorization: `Bearer ${await getIDToken()}`,
      },
      body: JSON.stringify(payload.newComment),
    })
      .then((response) => {
        if (response.ok) {
          return response.json()
        } else {
          throw new Error(response.statusText)
        }
      })
      .then((data) => {
        dispatch(
          updateComment({
            tab: payload.tab,
            commentId: payload.commentId,
            newComment: data,
          }),
        )
      })
      .catch((error) => {
        console.error('Error during updating a comment: ', error)
      })
  },
)

export const deleteCommentAsync = createAsyncThunk(
  'comments/deleteComment',
  async (
    payload: { tab: ConstructionTab; commentId: number },
    { dispatch },
  ) => {
    const url = `${BASE_URL}/api/comments/${payload.commentId}`
    fetch(url, {
      method: 'DELETE',
      headers: {
        'Access-Control-Allow-Origin': '*',
        Authorization: `Bearer ${await getIDToken()}`,
      },
    })
      .then((response) => {
        if (response.ok) {
          return response.json()
        } else {
          throw new Error(response.statusText)
        }
      })
      .then((data) => {
        dispatch(deleteComment({ tab: payload.tab, commentId: data.id }))
      })
      .catch((error) => {
        console.error('Error during deleting a comment: ', error)
      })
  },
)
